/**
 * Retirement Input Persistence Utility
 * Automatically saves and restores user's retirement planning inputs
 */

const STORAGE_KEY = 'retirement_last_inputs';

/**
 * Save retirement inputs to localStorage
 * @param {Object} inputs - The retirement inputs object
 */
export const saveRetirementInputs = (inputs) => {
  try {
    const dataToSave = {
      inputs,
      timestamp: new Date().toISOString(),
      version: '1.0' // For future migration if needed
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
    return true;
  } catch (error) {
    console.error('Error saving retirement inputs:', error);
    return false;
  }
};

/**
 * Load last saved retirement inputs from localStorage
 * @returns {Object|null} - Saved inputs or null if none found
 */
export const loadRetirementInputs = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return null;

    const { inputs, timestamp } = JSON.parse(saved);

    // Check if data is not too old (optional: 90 days)
    const savedDate = new Date(timestamp);
    const daysSinceSave = (Date.now() - savedDate) / (1000 * 60 * 60 * 24);

    if (daysSinceSave > 90) {
      console.log('Saved retirement inputs are older than 90 days, ignoring');
      return null;
    }

    return inputs;
  } catch (error) {
    console.error('Error loading retirement inputs:', error);
    return null;
  }
};

/**
 * Clear saved retirement inputs
 */
export const clearRetirementInputs = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    return true;
  } catch (error) {
    console.error('Error clearing retirement inputs:', error);
    return false;
  }
};

/**
 * Get info about saved inputs (for UI display)
 * @returns {Object|null} - Info about saved data
 */
export const getSavedInputsInfo = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return null;

    const { timestamp } = JSON.parse(saved);
    return {
      timestamp,
      lastSaved: new Date(timestamp).toLocaleString(),
      daysAgo: Math.floor((Date.now() - new Date(timestamp)) / (1000 * 60 * 60 * 24))
    };
  } catch (error) {
    return null;
  }
};

/**
 * Debounced save function to avoid too frequent localStorage writes
 * @param {Object} inputs - The retirement inputs
 * @param {number} delay - Delay in ms (default 2000)
 */
let saveTimeout = null;
export const debouncedSaveInputs = (inputs, delay = 2000) => {
  if (saveTimeout) {
    clearTimeout(saveTimeout);
  }

  saveTimeout = setTimeout(() => {
    saveRetirementInputs(inputs);
  }, delay);
};
